/* ============================================================
   CART DRAWER
   Utenzo-compatible version
   ============================================================ */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    /*
     * Close cart drawer with Escape key
     */
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    /*
     * Bind overlay close event
     */
    this.bindOverlay();

    /*
     * Connect Utenzo header cart button
     * with Shopify cart drawer.
     */
    this.setHeaderCartIconAccessibility();
  }


  /* ==========================================================
     HEADER CART BUTTON
     ========================================================== */

  setHeaderCartIconAccessibility() {
    /*
     * Utenzo header cart link:
     *
     * <a class="ch-action" href="/cart">
     */
    const cartLinks =
      document.querySelectorAll(
        '.ch-action[href="/cart"]'
      );

    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute(
        'role',
        'button'
      );

      cartLink.setAttribute(
        'aria-haspopup',
        'dialog'
      );

      /*
       * Prevent duplicate event listeners.
       */
      if (
        cartLink.dataset.cartDrawerBound ===
        'true'
      ) {
        return;
      }

      cartLink.dataset.cartDrawerBound =
        'true';


      /*
       * Mouse / normal link activation
       */
      cartLink.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          this.open(cartLink);
        }
      );


      /*
       * Space key support.
       *
       * ENTER does not need to be handled
       * manually because this is already
       * an <a> element and Enter triggers click.
       */
      cartLink.addEventListener(
        'keydown',
        (event) => {
          if (
            event.code.toUpperCase() ===
            'SPACE'
          ) {
            event.preventDefault();

            this.open(cartLink);
          }
        }
      );
    });
  }


  /* ==========================================================
     OVERLAY
     ========================================================== */

  bindOverlay() {
    const overlay =
      this.querySelector(
        '#CartDrawer-Overlay'
      );

    if (!overlay) return;

    /*
     * Prevent duplicate listeners.
     */
    if (
      overlay.dataset.cartDrawerBound ===
      'true'
    ) {
      return;
    }

    overlay.dataset.cartDrawerBound =
      'true';

    overlay.addEventListener(
      'click',
      () => {
        this.close();
      }
    );
  }


  /* ==========================================================
     OPEN CART DRAWER
     ========================================================== */

  open(triggeredBy = null) {
    /*
     * Remember which element opened
     * the drawer so focus can return
     * to it when drawer closes.
     */
    if (triggeredBy) {
      this.setActiveElement(
        triggeredBy
      );
    }

    /*
     * Cart note accessibility
     */
    const cartDrawerNote =
      this.querySelector(
        '[id^="Details-"] summary'
      );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute(
        'role'
      )
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }


    /*
     * Trigger drawer animation.
     */
    requestAnimationFrame(() => {
      this.classList.add(
        'animate',
        'active'
      );
    });


    /*
     * Trap keyboard focus inside
     * drawer after transition.
     */
    this.addEventListener(
      'transitionend',

      () => {
        const containerToTrapFocusOn =
          this.classList.contains(
            'is-empty'
          )
            ? this.querySelector(
                '.drawer__inner-empty'
              )
            : this.querySelector(
                '#CartDrawer'
              );

        const focusElement =
          this.querySelector(
            '.drawer__inner'
          ) ||
          this.querySelector(
            '.drawer__close'
          );

        if (
          containerToTrapFocusOn &&
          focusElement
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      },

      {
        once: true,
      }
    );


    /*
     * Prevent body scrolling while
     * drawer is open.
     */
    document.body.classList.add(
      'overflow-hidden'
    );
  }


  /* ==========================================================
     CLOSE CART DRAWER
     ========================================================== */

  close() {
    this.classList.remove('active');

    /*
     * Return focus to element that
     * originally opened the drawer.
     */
    if (this.activeElement) {
      removeTrapFocus(
        this.activeElement
      );
    } else {
      removeTrapFocus();
    }

    document.body.classList.remove(
      'overflow-hidden'
    );
  }


  /* ==========================================================
     CART NOTE ACCESSIBILITY
     ========================================================== */

  setSummaryAccessibility(
    cartDrawerNote
  ) {
    if (!cartDrawerNote) return;

    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    const content =
      cartDrawerNote.nextElementSibling;

    if (
      content &&
      content.id
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        content.id
      );
    }


    /*
     * Prevent duplicate event listeners.
     */
    if (
      cartDrawerNote.dataset
        .accessibilityBound === 'true'
    ) {
      return;
    }

    cartDrawerNote.dataset
      .accessibilityBound = 'true';


    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        const details =
          event.currentTarget.closest(
            'details'
          );

        if (!details) return;

        event.currentTarget.setAttribute(
          'aria-expanded',
          !details.hasAttribute('open')
        );
      }
    );


    if (cartDrawerNote.parentElement) {
      cartDrawerNote.parentElement
        .addEventListener(
          'keyup',
          onKeyUpEscape
        );
    }
  }


  /* ==========================================================
     RENDER CART DRAWER
     Called after Add to Cart
     ========================================================== */

  renderContents(parsedState) {
    if (!parsedState) return;

    this.productId =
      parsedState.id;


    /*
     * Remove empty state if product
     * has just been added.
     */
    this.classList.remove(
      'is-empty'
    );

    const drawerInner =
      this.querySelector(
        '.drawer__inner'
      );

    if (drawerInner) {
      drawerInner.classList.remove(
        'is-empty'
      );
    }


    /* ========================================================
       UPDATE CART DRAWER SECTION
       ======================================================== */

    const cartDrawerHTML =
      parsedState.sections &&
      parsedState.sections[
        'cart-drawer'
      ];

    if (cartDrawerHTML) {
      const parsedHTML =
        new DOMParser()
          .parseFromString(
            cartDrawerHTML,
            'text/html'
          );

      const newDrawer =
        parsedHTML.querySelector(
          '#CartDrawer'
        );

      const currentDrawer =
        this.querySelector(
          '#CartDrawer'
        );

      if (
        newDrawer &&
        currentDrawer
      ) {
        currentDrawer.innerHTML =
          newDrawer.innerHTML;
      }
    }


    /* ========================================================
       UPDATE UTENZO HEADER CART COUNT
       ======================================================== */

    this.updateHeaderCartCount();


    /*
     * #CartDrawer content has just
     * been replaced, therefore the
     * overlay element may also be new.
     */
    this.bindOverlay();


    /*
     * Header may have multiple
     * desktop/mobile cart buttons.
     */
    this.setHeaderCartIconAccessibility();


    /* ========================================================
       OPEN DRAWER
       ======================================================== */

    requestAnimationFrame(() => {
      this.open();
    });
  }


  /* ==========================================================
     UPDATE UTENZO HEADER CART COUNT
     ========================================================== */

  updateHeaderCartCount() {
    /*
     * Prefer Shopify theme route.
     *
     * Example:
     * routes.cart_url = "/cart"
     */
    const cartUrl =
      window.routes &&
      window.routes.cart_url
        ? `${window.routes.cart_url}.js`
        : '/cart.js';


    return fetch(cartUrl, {
      method: 'GET',

      headers: {
        Accept: 'application/json',
      },
    })

      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Cart request failed: ${response.status}`
          );
        }

        return response.json();
      })

      .then((cart) => {
        /*
         * Utenzo header counter:
         *
         * <span class="ch-cart-count">
         */
        document
          .querySelectorAll(
            '.ch-cart-count'
          )
          .forEach((counter) => {
            counter.textContent =
              cart.item_count;
          });


        /*
         * Keep drawer empty state
         * synchronized.
         */
        this.classList.toggle(
          'is-empty',
          cart.item_count === 0
        );

        return cart;
      })

      .catch((error) => {
        console.error(
          'Unable to update header cart count:',
          error
        );

        return null;
      });
  }


  /* ==========================================================
     GET SECTION INNER HTML
     ========================================================== */

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) return '';

    const parsedHTML =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );

    const element =
      parsedHTML.querySelector(
        selector
      );

    return element
      ? element.innerHTML
      : '';
  }


  /* ==========================================================
     SECTIONS REQUESTED BY PRODUCT FORM
     ========================================================== */

  getSectionsToRender() {
    /*
     * IMPORTANT:
     *
     * Do NOT add cart-icon-bubble here.
     *
     * Utenzo doesn't have:
     *
     * #cart-icon-bubble
     *
     * Utenzo uses:
     *
     * .ch-cart-count
     */
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }


  /* ==========================================================
     GET SECTION DOM
     ========================================================== */

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) return null;

    const parsedHTML =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );

    return parsedHTML.querySelector(
      selector
    );
  }


  /* ==========================================================
     STORE ACTIVE ELEMENT
     ========================================================== */

  setActiveElement(element) {
    this.activeElement =
      element || null;
  }
}


/* ============================================================
   REGISTER CART DRAWER
   ============================================================ */

if (
  !customElements.get(
    'cart-drawer'
  )
) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}


/* ============================================================
   CART DRAWER ITEMS
   ============================================================ */

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}


/* ============================================================
   REGISTER CART DRAWER ITEMS
   ============================================================ */

if (
  !customElements.get(
    'cart-drawer-items'
  )
) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}